<!--
AssistantChatList — left-sidebar list of the user's chat threads.

Presentational: props in (the chat summaries + the active id + loading), events
out (`create`, `select`, `delete`). It holds no network logic — AssistantPage
owns useChats and the delete confirmation. Owner-or-Full scoping is enforced
SERVER-SIDE; this component renders whatever list it is given.
-->
<template>
  <aside class="assistant-chat-list" data-testid="assistant-chat-list">
    <Button
      class="assistant-new-chat"
      data-testid="assistant-new-chat"
      :label="t('Nova conversa')"
      icon="pi pi-plus"
      severity="primary"
      :disabled="loading"
      @click="emit('create')"
    />
    <Button
      class="assistant-archived-toggle"
      data-testid="assistant-archived-toggle"
      :label="archivedView ? t('Ativas') : t('Arquivadas')"
      :icon="archivedView ? 'pi pi-list' : 'pi pi-inbox'"
      severity="secondary"
      text
      :disabled="loading"
      @click="emit('toggleArchived')"
    />

    <p
      v-if="chats.length === 0"
      class="assistant-chat-empty"
      data-testid="assistant-chat-empty"
    >
      {{ t('Nenhuma conversa ainda') }}
    </p>

    <ul v-else class="assistant-chat-items">
      <li
        v-for="(chat, index) in chats"
        :key="chat.id"
        class="assistant-chat-item"
        :class="{ 'assistant-chat-item-active': chat.id === activeChatId }"
        :data-testid="`assistant-chat-item`"
        :data-testid-index="index"
        :data-testid-chat-id="chat.id"
      >
        <template v-if="editingId === chat.id">
          <input-text
            class="assistant-chat-rename-input"
            data-testid="assistant-chat-rename-input"
            :elem-id="`assistant_rename_${chat.id}`"
            :fluid="true"
            v-model="draftTitle"
            @keyup.enter="commitRename(chat.id)"
            @keyup.esc="cancelRename"
          />
          <Button
            class="assistant-rename-confirm"
            data-testid="assistant-rename-confirm"
            icon="pi pi-check"
            severity="success"
            text
            rounded
            :aria-label="t('Salvar nome')"
            :disabled="loading || draftTitle.trim().length === 0"
            @click="commitRename(chat.id)"
          />
          <Button
            class="assistant-rename-cancel"
            data-testid="assistant-rename-cancel"
            icon="pi pi-times"
            severity="secondary"
            text
            rounded
            :aria-label="t('Cancelar')"
            @click="cancelRename"
          />
        </template>
        <template v-else>
          <button
            type="button"
            class="assistant-chat-item-select"
            data-testid="assistant-chat-item-select"
            @click="emit('select', chat.id)"
          >
            <span class="assistant-chat-item-title">{{ chat.title }}</span>
          </button>
          <Button
            class="assistant-rename-chat"
            data-testid="assistant-rename-chat"
            icon="pi pi-pencil"
            severity="secondary"
            text
            rounded
            :aria-label="t('Renomear conversa')"
            :disabled="loading"
            @click="startRename(chat)"
          />
          <Button
            class="assistant-archive-chat"
            data-testid="assistant-archive-chat"
            icon="pi pi-inbox"
            severity="secondary"
            text
            rounded
            :aria-label="t('Arquivar conversa')"
            :disabled="loading"
            @click="emit('archive', chat.id)"
          />
          <Button
            class="assistant-delete-chat"
            data-testid="assistant-delete-chat"
            icon="pi pi-trash"
            severity="danger"
            text
            rounded
            :aria-label="t('Excluir conversa')"
            :disabled="loading"
            @click="emit('delete', chat.id)"
          />
        </template>
      </li>
    </ul>
  </aside>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { ChatSummary } from '../../composables/aiAgentContract';

const props = defineProps<{
  chats: ChatSummary[];
  activeChatId: number | null;
  loading: boolean;
  archivedView: boolean;
}>();

const emit = defineEmits<{
  create: [];
  select: [chatId: number];
  rename: [chatId: number, title: string];
  delete: [chatId: number];
  archive: [chatId: number];
  toggleArchived: [];
}>();

const { t } = useI18n();

// Inline-rename state — which chat is being edited and its working title. Network
// access stays in the page (useChats); this component only emits `rename` on commit.
const editingId = ref<number | null>(null);
const draftTitle = ref<string>('');

function startRename(chat: ChatSummary): void {
  editingId.value = chat.id;
  draftTitle.value = chat.title;
}

function commitRename(chatId: number): void {
  const title = draftTitle.value.trim();
  const current = props.chats.find((c) => c.id === chatId);
  // Skip the request when blank or unchanged.
  if (title.length > 0 && title !== current?.title) {
    emit('rename', chatId, title);
  }
  cancelRename();
}

function cancelRename(): void {
  editingId.value = null;
  draftTitle.value = '';
}
</script>

<style scoped>
.assistant-chat-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 14rem;
  max-width: 18rem;
  border-right: 1px solid var(--p-surface-200);
  padding-right: 0.75rem;
}

.assistant-new-chat {
  width: 100%;
}

.assistant-chat-empty {
  color: var(--p-text-muted-color);
  text-align: center;
  font-size: 0.85rem;
  margin: 1rem 0;
}

.assistant-chat-items {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  overflow-y: auto;
  max-height: 60vh;
}

.assistant-chat-item {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  border-radius: 0.5rem;
}

.assistant-chat-item-active {
  background: var(--p-surface-200);
}

.assistant-chat-item-select {
  flex: 1;
  display: flex;
  align-items: center;
  background: none;
  border: none;
  padding: 0.5rem 0.5rem;
  cursor: pointer;
  text-align: left;
  color: var(--p-text-color);
  font: inherit;
}

.assistant-chat-item-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
